import { DateTime, type NaiveDate, type Utc } from "@tento-chrono";

import type { Option } from "../../core/option.ts";
import type { SheetsNetwork } from "../../wire/rows-network.ts";
import type { EventItem } from "./event-item.ts";
import { Patch } from "../../rows/row-patch.ts";

/**
 * A change to an existing event, field by field: each field the patch names
 * is SET to a value or — where the server can clear it — CLEARED
 * (`Patch.clear`); a field it does not name is left as it is. The one
 * change the event verbs take (`events.update`, `updateDurable`,
 * `updateOccurrence`, `EventsFacade.applyUpdate`):
 *
 * ```ts
 * const patch = EventPatch.of({ title: "Standup", category: Patch.clear });
 * await client.events.update(event, patch, Actor.EXTERNAL);
 * ```
 *
 * A patch has ONE wire form (`encode`) and the event it paints locally is
 * derived from that body (`paint`, `EventPatch.paintWire`), so what the row
 * shows once the server accepted the write is what the server stored: a
 * field the body leaves out is left alone on both sides, a `null` clears on
 * both, a value sets on both.
 *
 * Only the fields the server clears on a `null` accept `Patch.clear`:
 * `description`, `location`, `category`, `suggestedCategory`,
 * `useSuggestedCategory`, `timezone`, `recurrence`, `inviteeEmails`. The
 * rest are set-only (`title`, `time`, `startDate` + `endDate` — given
 * together —, `responseStatus`, `conferenceOperation`, `scope`). The
 * patch is immutable.
 */
export class EventPatch {
  readonly #fields: EventPatch.Fields;

  private constructor(fields: EventPatch.Fields) {
    this.#fields = Object.freeze({ ...fields });
  }

  /** The patch that sets / clears exactly the fields `fields` names. */
  static of(fields: EventPatch.Fields): EventPatch {
    return new EventPatch(fields);
  }

  /**
   * The patch a wire body (`Network.EventUpdate`, as a command line or a
   * tool argument carries it) describes: a value sets, `null` clears, an
   * absent key leaves the field. Throws `TypeError` for a body the verbs
   * cannot send: a `null` on a set-only field, or a `startDate` without its
   * `endDate` (or the reverse).
   */
  static fromWire(body: SheetsNetwork.EventUpdate): EventPatch {
    for (const key of SET_ONLY) {
      if (Object.prototype.hasOwnProperty.call(body, key) && body[key] === null) {
        throw new TypeError(`EventPatch.fromWire: ${key} cannot be cleared (set it, or leave it out)`);
      }
    }
    const common: EventPatch.Draft = {};
    if (body.title != null) common.title = body.title;
    if (body.time != null) common.time = body.time;
    if (body.responseStatus != null) common.responseStatus = body.responseStatus;
    if (body.conferenceOperation != null) common.conferenceOperation = body.conferenceOperation;
    if (body.scope != null) common.scope = body.scope;
    if (named(body, "description")) common.description = body.description ?? Patch.clear;
    if (named(body, "location")) common.location = body.location ?? Patch.clear;
    if (named(body, "category")) common.category = body.category ?? Patch.clear;
    if (named(body, "suggestedCategory")) common.suggestedCategory = body.suggestedCategory ?? Patch.clear;
    if (named(body, "useSuggestedCategory")) common.useSuggestedCategory = body.useSuggestedCategory ?? Patch.clear;
    if (named(body, "timezone")) common.timezone = body.timezone ?? Patch.clear;
    if (named(body, "recurrence")) common.recurrence = body.recurrence ?? Patch.clear;
    if (named(body, "inviteeEmails")) common.inviteeEmails = body.inviteeEmails ?? Patch.clear;
    const startDate = body.startDate ?? null;
    const endDate = body.endDate ?? null;
    if ((startDate == null) !== (endDate == null)) {
      throw new TypeError("EventPatch.fromWire: startDate and endDate are set together");
    }
    return startDate != null && endDate != null
      ? new EventPatch({ ...common, startDate, endDate })
      : new EventPatch(common);
  }

  /** Whether the patch names no field (sending it changes nothing). */
  get isEmpty(): boolean {
    return Object.keys(this.#fields).length === 0;
  }

  /** The fields the patch names, as `of` took them. */
  get fields(): EventPatch.Fields {
    return this.#fields;
  }

  /** This patch with `fields` set / cleared over it (a key `fields` names wins). */
  with(fields: EventPatch.Fields): EventPatch {
    return new EventPatch({ ...this.#fields, ...fields });
  }

  /**
   * The wire body (`Network.EventUpdate`) — the one encoder: a set field
   * carries its value, a cleared one `null`, a field the patch does not
   * name no key. A fresh object on every call.
   */
  encode(): SheetsNetwork.EventUpdate {
    const f = this.#fields;
    const body: SheetsNetwork.EventUpdate = {};
    if (f.title !== undefined) body.title = f.title;
    if (f.time !== undefined) body.time = { start: f.time.start, end: f.time.end };
    if (f.startDate !== undefined && f.endDate !== undefined) {
      body.startDate = f.startDate;
      body.endDate = f.endDate;
    }
    if (f.description !== undefined) body.description = wireOf(f.description);
    if (f.location !== undefined) body.location = wireOf(f.location);
    if (f.category !== undefined) body.category = wireOf(f.category);
    if (f.suggestedCategory !== undefined) body.suggestedCategory = wireOf(f.suggestedCategory);
    if (f.useSuggestedCategory !== undefined) body.useSuggestedCategory = wireOf(f.useSuggestedCategory);
    if (f.timezone !== undefined) body.timezone = wireOf(f.timezone);
    if (f.recurrence !== undefined) body.recurrence = listOf(f.recurrence);
    if (f.inviteeEmails !== undefined) body.inviteeEmails = listOf(f.inviteeEmails);
    if (f.responseStatus !== undefined) body.responseStatus = f.responseStatus;
    if (f.conferenceOperation !== undefined) body.conferenceOperation = f.conferenceOperation;
    if (f.scope !== undefined) body.scope = f.scope;
    return body;
  }

  /** `event` as this patch leaves it — `EventPatch.paintWire` of `encode()`. */
  paint(event: EventItem): EventItem {
    return EventPatch.paintWire(event, this.encode()).updated;
  }
}

/**
 * Types and values that go with `EventPatch`: `CommonFields`, `DateFields`,
 * `Fields`, `Draft`, `paintWire`.
 */
export namespace EventPatch {
  /** The fields every patch may name (all but the all-day date pair). */
  export interface CommonFields {
    readonly title?: string;
    readonly time?: SheetsNetwork.DateTimeRange;
    readonly description?: Patch.Clearable<string>;
    readonly location?: Patch.Clearable<string>;
    readonly category?: Patch.Clearable<string>;
    readonly suggestedCategory?: Patch.Clearable<string>;
    readonly useSuggestedCategory?: Patch.Clearable<boolean>;
    readonly timezone?: Patch.Clearable<string>;
    readonly recurrence?: Patch.Clearable<readonly string[]>;
    readonly inviteeEmails?: Patch.Clearable<readonly string[]>;
    readonly responseStatus?: string;
    readonly conferenceOperation?: NonNullable<SheetsNetwork.EventUpdate["conferenceOperation"]>;
    readonly scope?: NonNullable<SheetsNetwork.EventUpdate["scope"]>;
  }

  /** The all-day date pair: both or neither. */
  export type DateFields =
    | { readonly startDate?: never; readonly endDate?: never }
    | { readonly startDate: NaiveDate; readonly endDate: NaiveDate };

  /** What `EventPatch.of` takes: a set value or `clear` per named field. */
  export type Fields = CommonFields & DateFields;

  /** A mutable bag of common fields, for a form that gathers a patch field by field. */
  export type Draft = { -readonly [K in keyof CommonFields]: CommonFields[K] };

  /**
   * `event` with an encoded body applied — the one local projection of an
   * event write, used for the optimistic paint and for folding an accepted
   * write into the confirmed event. Per key, exactly as the server applies
   * the body: absent (or `undefined`) leaves the field; `null` clears a
   * clearable field and leaves a set-only one (the server ignores it); a
   * value sets it. The all-day pair applies only together. `didUpdate`:
   * whether the body named a field it paints.
   */
  export function paintWire(
    event: EventItem,
    body: SheetsNetwork.EventUpdate,
  ): { updated: EventItem; didUpdate: boolean } {
    let current: EventItem = event;
    let didUpdate = false;
    if (body.title != null) {
      current = current.updateTitle(body.title);
      didUpdate = true;
    }
    if (body.time != null) {
      current = current.updateTime(DateTime.Range.from<Utc>(body.time));
      didUpdate = true;
    }
    if (body.startDate != null && body.endDate != null) {
      current = current.updateDateRange(body.startDate, body.endDate);
      didUpdate = true;
    }
    if (named(body, "description")) {
      current = current.updateDescription(body.description ?? null);
      didUpdate = true;
    }
    if (named(body, "location")) {
      current = current.updateLocation(body.location ?? null);
      didUpdate = true;
    }
    if (named(body, "category")) {
      current = current.updateCategory(body.category ?? null);
      didUpdate = true;
    }
    if (named(body, "suggestedCategory")) {
      current = current.updateSuggestedCategory(body.suggestedCategory ?? null);
      didUpdate = true;
    }
    if (named(body, "useSuggestedCategory")) {
      current = current.updateUseSuggestedCategory(body.useSuggestedCategory ?? null);
      didUpdate = true;
    }
    if (named(body, "timezone")) {
      current = current.updateTimezone(body.timezone ?? null);
      didUpdate = true;
    }
    if (named(body, "recurrence")) {
      current = current.updateRecurrence(body.recurrence ?? null);
      didUpdate = true;
    }
    if (named(body, "inviteeEmails")) {
      current = current.updateInviteeEmails(body.inviteeEmails ?? null);
      didUpdate = true;
    }
    if (body.responseStatus != null) {
      current = current.updateResponseStatus(body.responseStatus);
      didUpdate = true;
    }
    return { updated: current, didUpdate };
  }
}

/** The body keys a `null` may not clear (the server ignores or refuses it). */
const SET_ONLY = [
  "title",
  "time",
  "startDate",
  "endDate",
  "responseStatus",
  "conferenceOperation",
  "scope",
] as const;

/** A clearable field's wire value: `null` for `clear`. */
function wireOf<T>(value: Patch.Clearable<T>): Option<T> {
  return Patch.wire(value);
}

/** A clearable list's wire value: a copy, or `null` for `clear`. */
function listOf(value: Patch.Clearable<readonly string[]>): Option<string[]> {
  return value === Patch.clear ? null : [...value];
}

/** Whether `body` names `key` (present and not `undefined`). */
function named(body: SheetsNetwork.EventUpdate, key: keyof SheetsNetwork.EventUpdate): boolean {
  return Object.prototype.hasOwnProperty.call(body, key) && body[key] !== undefined;
}
